import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { Retailer } from '@src/app/model/retailer';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { feSortToBeSort } from '@src/app/components/utils/utils';
import { cloneDeep } from 'lodash';
import { map, take } from 'rxjs/operators';
import { Id } from '@ess/jg-rule-executor';
import { CatalogInput, RetailerInput, SubjectType } from '@src/app/generated/globalTypes';
import { PortalModuleCategory } from '@src/app/model/portal-module-category';
import { Sort } from '@angular/material/sort';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';

@Component({
  selector: 'app-retailer-list',
  templateUrl: './retailer-list.component.html',
  styleUrls: ['./retailer-list.component.scss'],
  standalone: false,
})
export class RetailerListComponent implements OnInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  public displayedColumns: string[] = ['id', 'name', 'email', 'location', 'retailerOptions.currencyOptions.currency', 'action'];
  public currentRetailerItems: Retailer[];
  public hasNextPage: boolean;
  public pageSize: number = 10;
  public currentPage: number = 0;
  public count: number = 0;

  private _retailerFailMsg: string = 'retailers.upload.failure';
  private _skipFields: string[] = ['id', 'safetyEnvironment.invariants'];

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
  ) {
  }

  public ngOnInit(): void {
    this._apolloService.getAllRetailers$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((retailersAndPageData) => {
        this.currentRetailerItems = retailersAndPageData.retailers;
        this.count = retailersAndPageData.count;
        this.hasNextPage = retailersAndPageData.hasNextPage;
      });
  }

  public paginate(toPage: number): void {
    this.currentPage = toPage;
    this._getRetailers(this.pageSize, this.currentPage);
  }

  public setPageSize(newPageSize: string): void {
    // the new page size is a string because of the select component; if we somehow get a non-integer value, ignore it and reset the value.
    const pageSize = Number(newPageSize);
    if (pageSize > 0) {
      this.pageSize = pageSize;
      this.currentPage = 0;
      this._getRetailers(this.pageSize, this.currentPage);
    } else {
      console.error('Received non-integer value for page size');
    }
  }

  public openAddRetailerDialog(): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'retailer',
      new Retailer(),
      'retailers.upload.header',
      'buttons.submit'
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      this._apolloService.newRetailer$.bind(this._apolloService),
      this._translateService.instant(this._retailerFailMsg),
      (() => this._onSuccess('upload')).bind(this)
    );
  }

  public openCopyRetailerDialog(retailer: Retailer): void {
    const inputRetailer = cloneDeep(retailer);
    inputRetailer.name = 'NEW_NAME';
    inputRetailer.location = 'NEW_LOCATION';
    inputRetailer.retailerOptions.installationSKUs = undefined;
    inputRetailer.retailerOptions.installationPrices = undefined;
    inputRetailer.retailerOptions.preconfigurations = [];

    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'retailer',
      inputRetailer,
      'retailers.copy.header',
      'buttons.submit'
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      (json => this._doCopy$(json, retailer)).bind(this),
      this._translateService.instant(this._retailerFailMsg),
      (() => this._onSuccess('copy')).bind(this)
    );
  }

  private _onSuccess(key: string): void {
    this._apolloService.getAllRetailersRefresh();
    window.alert(
      this._translateService.instant('retailers.' + key + '.success')
      + this._translateService.instant('retailers.todo')
      + this._translateService.instant('retailers.' + key + '.todo')
    );
  }

  /**
   * Call copy retailer mutation
   */
  private _doCopy$(json: JSON, originalRetailer: Retailer): Observable<boolean> {
    return this._apolloService.copyRetailer$(originalRetailer.id, json)
      .pipe(map(newRetailer => !!newRetailer));
  }

  public openEditRetailerJsonDialog(retailer: Retailer): void {
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'retailer',
      retailer ? retailer : new Retailer(),
      'retailers.edit.header',
      'buttons.submit',
      this._skipFields
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      (json => this._apolloService.updateRetailer$(retailer.id, json)).bind(this._apolloService),
      this._translateService.instant(this._retailerFailMsg),
      this._apolloService.getAllRetailersRefresh.bind(this._apolloService)
    );
  }

  public openEditRetailerFormDialog(retailer: Retailer): void {
    const dialogRef = TypedFormModal.openTypedFormModalDialog(
      this._dialog, new TypedFormModalDataObject(
        'retailer',
        SubjectType.RetailerType,
        retailer,
        retailer.id,
        this._skipFields,
        'retailers.edit.header',
      )
    );
    TypedFormModal.useFormDialog(
      dialogRef,
      (
        (edited: RetailerInput) =>
          this._apolloService.updateRetailer$(retailer.id, Retailer.toJsonInput(edited))
      ).bind(this._apolloService),
      this._translateService.instant(this._retailerFailMsg),
      this._apolloService.getAllRetailersRefresh.bind(this._apolloService)
    );
  }

  public getRetailerApiKey(id: Id<Retailer>): void {
    this._apolloService.getRetailerApiKey$(id)
      .pipe(take(1), takeUntil(this._componentDestroyed$))
      .subscribe((apiKey) => {
        window.alert(`Api key is: ${apiKey}`);
      });
  }

  private _getRetailers(pageSize: number, currentPage: number, sort?: Sort): void {
    const beSort = sort ? feSortToBeSort(sort) : undefined;
    this._apolloService.getAllRetailers$(pageSize, pageSize * currentPage, beSort ? [beSort] : undefined)
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((retailersAndPageData) => {
        this.currentRetailerItems = retailersAndPageData.retailers;
        this.count = retailersAndPageData.count;
        this.hasNextPage = retailersAndPageData.hasNextPage;
      });
  }

  public sortChange(sortState: Sort): void {
    this._getRetailers(this.pageSize, this.currentPage, sortState);
  }

  private _filterSettingsKeysToIds(catalog: CatalogInput, categories: PortalModuleCategory[]): CatalogInput {
    const updatedCatalog = cloneDeep(catalog);
    updatedCatalog.filterSettings.values = updatedCatalog.filterSettings.values.map((fs) => {
      const categoryId = categories.find(mc => mc.reference === fs.key).id;
      return { key: categoryId.toString(), value: fs.value };
    });
    return updatedCatalog;
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }
}
